import { useEffect } from 'react';

/**
 * Update Google Consent Mode v2 based on accepted cookie categories.
 * Maps our cookie consent categories to Google's consent types.
 */
function updateGtagConsent(acceptedCategories: string[]) {
  const win = window as unknown as {
    gtag?: (...args: unknown[]) => void;
  };
  if (typeof win.gtag !== 'function') return;

  const hasAnalytics = acceptedCategories.includes('analytics');
  const hasMarketing = acceptedCategories.includes('marketing');
  const hasPreferences = acceptedCategories.includes('preferences');

  win.gtag('consent', 'update', {
    analytics_storage: hasAnalytics ? 'granted' : 'denied',
    ad_storage: hasMarketing ? 'granted' : 'denied',
    ad_user_data: hasMarketing ? 'granted' : 'denied',
    ad_personalization: hasMarketing ? 'granted' : 'denied',
    personalization_storage: hasPreferences ? 'granted' : 'denied',
  });
}

/**
 * Client-only initializer for the cookie consent banner (vanilla-cookieconsent v3).
 * - Loads the UMD script from CDN
 * - Initializes categories: necessary (read-only), preferences, analytics, marketing
 * - Dispatches a `cookieconsent:updated` CustomEvent on consent changes
 */
export function CookieConsent() {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const existingScript = document.querySelector(
      'script[data-cookieconsent="true"]'
    ) as HTMLScriptElement | null;

    function initIfAvailable() {
      const win = window as unknown as {
        CookieConsent?: { run?: (config: unknown) => unknown; getUserPreferences?: () => unknown };
      };
      if (!win.CookieConsent || typeof win.CookieConsent.run !== 'function') return false;

      const CC = win.CookieConsent as unknown as {
        run: (config: unknown) => unknown;
        // Optional API in case it's available
        getUserPreferences?: () =>
          | { acceptedCategories?: string[]; categories?: Record<string, boolean> }
          | undefined;
      };

      CC.run({
        // Use a custom cookie name so first deployment shows the banner everywhere
        cookie: {
          name: 'tr_cc',
          expiresAfterDays: 180,
        },
        // Bump the revision to re-prompt users after policy changes
        revision:
          Number(
            (import.meta as unknown as { env?: Record<string, string> })?.env
              ?.VITE_COOKIECONSENT_REVISION
          ) || 1,
        // Ensure banner is shown automatically when no consent stored
        autoShow: true,
        guiOptions: {
          consentModal: {
            layout: 'box',
            position: 'bottom right',
            flipButtons: false,
          },
          preferencesModal: {
            layout: 'box',
            position: 'right',
          },
        },
        categories: {
          necessary: {
            enabled: true,
            readOnly: true,
          },
          preferences: {
            enabled: false,
          },
          analytics: {
            enabled: false,
          },
          marketing: {
            enabled: false,
          },
        },
        language: {
          default: 'en',
          translations: {
            en: {
              consentModal: {
                title: 'We value your privacy',
                description:
                  'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. You can manage your preferences.',
                acceptAllBtn: 'Accept all',
                acceptNecessaryBtn: 'Reject non-essential',
                showPreferencesBtn: 'Manage preferences',
              },
              preferencesModal: {
                title: 'Cookie preferences',
                acceptAllBtn: 'Accept all',
                acceptNecessaryBtn: 'Reject non-essential',
                savePreferencesBtn: 'Save preferences',
                sections: [
                  {
                    title: 'Strictly necessary',
                    description:
                      'These cookies are essential for the website to function properly and cannot be disabled.',
                    linkedCategory: 'necessary',
                  },
                  {
                    title: 'Preferences',
                    description: 'These cookies allow the website to remember choices you make.',
                    linkedCategory: 'preferences',
                  },
                  {
                    title: 'Analytics',
                    description: 'These cookies help us understand how our website is used.',
                    linkedCategory: 'analytics',
                  },
                  {
                    title: 'Marketing',
                    description:
                      'These cookies are used to deliver advertising more relevant to you and your interests.',
                    linkedCategory: 'marketing',
                  },
                ],
              },
            },
          },
        },
        onConsent: ({ cookie }: { cookie?: { categories?: Record<string, boolean> } }) => {
          try {
            const categories = cookie?.categories || {};
            const accepted = Object.entries(categories)
              .filter(([, enabled]) => Boolean(enabled))
              .map(([key]) => key);
            updateGtagConsent(accepted);
            document.dispatchEvent(
              new CustomEvent('cookieconsent:updated', {
                detail: { acceptedCategories: accepted },
              })
            );
          } catch {}
        },
        onChange: ({ cookie }: { cookie?: { categories?: Record<string, boolean> } }) => {
          try {
            const categories = cookie?.categories || {};
            const accepted = Object.entries(categories)
              .filter(([, enabled]) => Boolean(enabled))
              .map(([key]) => key);
            updateGtagConsent(accepted);
            document.dispatchEvent(
              new CustomEvent('cookieconsent:updated', {
                detail: { acceptedCategories: accepted },
              })
            );
          } catch {}
        },
      });

      // Expose object for debugging (e.g., window.__cookieConsent)
      try {
        (window as unknown as { __cookieConsent?: unknown }).__cookieConsent = CC;
      } catch {}

      // Fire initial state event so listeners can act on persisted consent
      try {
        const prefs = CC.getUserPreferences?.();
        const accepted =
          prefs?.acceptedCategories && Array.isArray(prefs.acceptedCategories)
            ? (prefs.acceptedCategories as string[])
            : Object.entries(prefs?.categories || ({} as Record<string, boolean>))
                .filter(([, enabled]) => Boolean(enabled))
                .map(([key]) => key);
        updateGtagConsent(accepted);
        document.dispatchEvent(
          new CustomEvent('cookieconsent:updated', {
            detail: { acceptedCategories: accepted },
          })
        );
      } catch {}

      return true;
    }

    if (existingScript) {
      // Script already present, try to init immediately; if not ready, wait for load
      const initialized = initIfAvailable();
      if (!initialized) {
        existingScript.addEventListener('load', () => initIfAvailable(), {
          once: true,
        } as unknown as boolean);
      }
      return;
    }

    const script = document.createElement('script');
    script.src =
      'https://cdn.jsdelivr.net/npm/vanilla-cookieconsent@3.1.0/dist/cookieconsent.umd.js';
    script.async = true;
    script.defer = true;
    script.setAttribute('data-cookieconsent', 'true');
    script.onload = () => {
      initIfAvailable();
    };
    document.head.appendChild(script);

    return () => {
      // Do not remove script to preserve user preferences across navigations
    };
  }, []);

  return null;
}
